import {
    createContext,
    memo,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

import { APP_CONTAINER_ID } from "@/constants/elementsIds";
import useDebounce from "@/hooks/useDebounce";
import { DeviceTypes, useDeviceType } from "@/hooks/useDeviceType";

export interface ThemeContextType {
    handleFocus: () => void;
    handleBlur: () => void;
}

const defaultThemeContext: ThemeContextType = {
    handleFocus: () => null,
    handleBlur: () => null,
};

export const OverflowContext = createContext<ThemeContextType>(defaultThemeContext);

const OverflowProvider = ({ children }: PropsWithChildren) => {
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const { deviceOS, isAndroidTT } = useDeviceType();

    const isIOS = deviceOS === DeviceTypes.IOS;

    const debouncedIsOpen = useDebounce(isKeyboardOpen, 500);

    useEffect(() => {
        if (!debouncedIsOpen) {
            const appContainerRef = document.getElementById(APP_CONTAINER_ID);

            appContainerRef?.classList.add("overflow-auto");
            if (isAndroidTT) {
                appContainerRef?.classList.remove("pb-[500px]");
            }
        }
    }, [debouncedIsOpen, isAndroidTT]);

    const handleFocus = useCallback(() => {
        setIsKeyboardOpen(true);
        const appContainerRef = document.getElementById(APP_CONTAINER_ID);

        appContainerRef?.classList.remove("overflow-auto");
        if (isAndroidTT) {
            appContainerRef?.classList.add("pb-[500px]");
        }
    }, [isAndroidTT]);

    const handleBlur = useCallback(() => {
        setIsKeyboardOpen(false);
    }, []);

    return (
        <OverflowContext.Provider
            value={
                isIOS
                    ? {
                          handleFocus,
                          handleBlur,
                      }
                    : defaultThemeContext
            }
        >
            {children}
        </OverflowContext.Provider>
    );
};

export const useOverflow = () => {
    return useContext(OverflowContext);
};

export default memo(OverflowProvider);
